// Дым из-под паровоза: два клуба по бокам машины.
//
// Клубы лежат под картинкой машины, поэтому из-под неё выходят только их края — как будто
// пар бьёт из-под котла наружу. Правый клуб — тот же самый, отражённый по горизонтали:
// так их пара стоит и в сборной сцене художника, см. SMOKE_SKELETON.
//
// Раскадровка идёт ровным клубом от первого кадра до последнего: она не появляется и не тает,
// а кипит с одной плотностью. Поэтому плотность задаёт сам узел — дым набирает её на входе
// и теряет на выходе, иначе он возникал бы и пропадал скачком. По той же причине клуб можно
// крутить по кругу: конец раскадровки ничем не отличается от её начала.
//
// Дым и звук паровоза — одно движение, см. ADR-49. Пар без голоса выглядит немым, голос
// без пара — беспричинным, поэтому пускает их одна строка, и держится дым ровно столько,
// сколько идёт голос. Длину дым спрашивает у звука, а не хранит свою.
//
// Клубов в игре два: тихий пар стоящей машины и густой пар на полном ходу. Скелет каждому
// свой, а всё прочее у них одно, поэтому скелет и задаётся снаружи.
//
// Координаты здесь те же, что у барабанов: это пиксели картинки барабанов.

import { Container } from 'pixi.js';

import { REELS_CONTENT } from '../assets/catalog';
import type { AssetManager, Spine, SpineSkeletonSource } from '../assets/manager';
import type { SoundVoice } from '../sound/engine';
import { sfx, sfxSeconds, type TrainSfx } from '../sound/sound';

/** Ширина клуба дыма в координатах картинки барабанов. Подобрана по референсу. */
const WIDTH: number = 680;

/** Насколько середина клуба отстоит от середины машины вбок. */
const OFFSET_X: number = 570;

/** Высота середины клуба: нижняя треть машины, вровень с её колёсами. */
const CENTER_Y: number = 1430;

/** Сколько дым набирает плотность на входе и сколько теряет на выходе. */
const FADE_SECONDS: number = 0.35;

/** Дым из-под паровоза. */
export class Smoke {
  readonly view: Container = new Container();

  readonly #source: SpineSkeletonSource<'full'>;
  readonly #puffs: Spine[];

  /** Сколько идёт раскадровка одного клуба. Ниже этого дым не бывает. */
  readonly #length: number;

  /** До какой секунды дыму держаться. Её задаёт голос, под который дым пущен. */
  #burning = 0;

  /** Идущий голос. Null — звука нет, и уводить нечего. */
  #cue: SoundVoice | null = null;

  #playing = false;
  #seconds = 0;

  /**
   * @param source Скелет клуба. В покое машина пыхает одним, на полном ходу — другим,
   * см. SMOKE_SKELETON и RUSH_SMOKE_SKELETON.
   */
  constructor(assets: AssetManager, source: SpineSkeletonSource<'full'>) {
    const centerX: number = REELS_CONTENT.x + REELS_CONTENT.width / 2;

    this.#source = source;

    this.#puffs = [-1, 1].map((side: number) => {
      const puff: Spine = assets.spine(source);
      const scale: number = WIDTH / puff.skeleton.data.width;

      // Клуб нарисован вокруг начала координат, поэтому оно и есть его середина.
      // Отрицательный масштаб по горизонтали отражает правый клуб вокруг этой же середины.
      puff.position.set(centerX + side * OFFSET_X, CENTER_Y);
      puff.scale.set(side * scale, scale);

      this.view.addChild(puff);

      return puff;
    });

    this.#length = this.#puffs[0].skeleton.data.findAnimation(source.animations.full)?.duration ?? 0;
    this.view.visible = false;
  }

  /**
   * Пускает дым вместе с голосом паровоза.
   *
   * Держится он ровно столько, сколько идёт голос, и клуб крутится по кругу, пока тот
   * не кончится. Короткий голос дым не обрезает: клуб проходит раскадровку целиком, иначе
   * пар пропал бы у игрока на глазах.
   *
   * Идущий дым новый голос не гасит и не пускает заново: пар и так стоит, ему только
   * переставляют конец. Иначе смена голоса посреди поездки проваливала бы пар до прозрачного.
   *
   * Незагруженный звук длины не называет, и тогда дым идёт один клуб, как шёл до звука.
   *
   * @param cue Голос, под который идёт дым. Порознь с ним дым не пускают, см. ADR-49.
   */
  start(cue: TrainSfx): void {
    // Паровоз один, и голос у него один: прежний уходит, а не звучит вторым.
    this.#cue?.fadeOut(FADE_SECONDS);
    this.#cue = sfx(cue);

    if (!this.#playing) {
      this.#playing = true;
      this.#seconds = 0;

      this.view.visible = true;
      this.view.alpha = 0;

      for (const puff of this.#puffs)
        puff.state.setAnimation(0, this.#source.animations.full, true);
    }

    this.#burning = this.#seconds + Math.max(sfxSeconds(cue), this.#length);
  }

  /**
   * Пускает дым и держит его, пока не позовут stop.
   *
   * Так идёт пар в поездке: голос там крутится по кругу, конца у него нет, и конец дыму
   * ставит не звук, а прибытие поезда, см. ui/ride.ts.
   */
  hold(cue: TrainSfx): void {
    this.start(cue);
    this.#burning = Infinity;
  }

  /**
   * Уводит дым: пар тает, а с ним уходит и голос.
   *
   * Пропасть разом дым не может — ничего в игре не обрывается на полуслове, см. ADR-50.
   * Поэтому конец дыма переставляется на выход, а идёт этот выход ровно столько, сколько
   * нужно нынешней плотности: густой пар тает своё время, едва занявшийся — почти сразу.
   *
   * Столько же уходит и голос: они кончаются вместе, как и начинались, см. ADR-49.
   */
  stop(): void {
    if (!this.#playing)
      return;

    const going: number = this.view.alpha * FADE_SECONDS;

    this.#cue?.fadeOut(going);
    this.#cue = null;

    this.#burning = Math.min(this.#burning, this.#seconds + going);
  }

  update(seconds: number): void {
    if (!this.#playing)
      return;

    this.#seconds += seconds;

    if (this.#seconds >= this.#burning) {
      this.#hide();
      return;
    }

    // Плотность набирается на входе и теряется на выходе, между ними держится единица.
    this.view.alpha = Math.min(this.#seconds, this.#burning - this.#seconds, FADE_SECONDS) / FADE_SECONDS;
  }

  /** Снимает растаявший дым со сцены. */
  #hide(): void {
    this.#playing = false;
    this.#cue = null;
    this.view.visible = false;
  }
}
